import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface AccessibilityCoverageRow {
  channel_id: string
  channel_name: string
  ad_count: number
  subtitles_count: number
  bsl_count: number
}

export interface AccessibilityTrendsRow {
  stat_date: string
  ad_count: number
  subtitles_count: number
  bsl_count: number
}

export function useAdminAccessibilityCoverage() {
  return useQuery({
    queryKey: ['admin-accessibility-coverage'],
    queryFn: async (): Promise<AccessibilityCoverageRow[]> => {
      const { data, error } = await supabase.rpc('get_admin_accessibility_coverage')
      if (error) throw error
      const rows = (Array.isArray(data) ? data : []) as Array<{
        channel_id: string
        channel_name: string
        ad_count: number | string
        subtitles_count: number | string
        bsl_count: number | string
      }>
      return rows.map((r) => ({
        channel_id: r.channel_id,
        channel_name: r.channel_name,
        ad_count: Number(r.ad_count ?? 0),
        subtitles_count: Number(r.subtitles_count ?? 0),
        bsl_count: Number(r.bsl_count ?? 0),
      }))
    },
    staleTime: 60_000,
  })
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export function useAdminAccessibilityTrends(fromDate: Date, toDate: Date) {
  const fromStr = toISODate(fromDate)
  const toStr = toISODate(toDate)

  return useQuery({
    queryKey: ['admin-accessibility-trends', fromStr, toStr],
    queryFn: async (): Promise<AccessibilityTrendsRow[]> => {
      const { data, error } = await supabase.rpc('get_admin_accessibility_trends', {
        p_from_date: fromStr,
        p_to_date: toStr,
      })
      if (error) throw error
      const rows = (Array.isArray(data) ? data : []) as Array<{
        stat_date: string
        ad_count: number | string
        subtitles_count: number | string
        bsl_count: number | string
      }>
      return rows.map((r) => ({
        stat_date: r.stat_date,
        ad_count: Number(r.ad_count ?? 0),
        subtitles_count: Number(r.subtitles_count ?? 0),
        bsl_count: Number(r.bsl_count ?? 0),
      }))
    },
    staleTime: 60_000,
  })
}
